import { useState } from 'react';
import { Send, Mail, Phone, MapPin } from 'lucide-react';
import { enquiryTypes, contactInfo } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    enquiryType: '',
    projectDate: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact-enquiry`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        enquiry_type: formData.enquiryType,
        project_date: formData.projectDate,
        message: formData.message
      });

      if (response.data) {
        toast({
          title: "Enquiry Submitted Successfully",
          description: "Thank you for your interest. We'll be in touch within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          enquiryType: '',
          projectDate: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast({
        title: "Submission Error",
        description: "There was a problem submitting your enquiry. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Let's discuss your creative project. Discreet, professional enquiries only.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1">
            <h2 className="text-2xl font-semibold text-white mb-8">Contact Information</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Mail className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a href={`mailto:${contactInfo.email}`} className="text-gray-400 hover:text-red-400 transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Phone className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Phone</h3>
                  <p className="text-gray-400">{contactInfo.phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-600/10 flex items-center justify-center">
                  <MapPin className="text-red-500" size={20} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Location</h3>
                  <p className="text-gray-400">{contactInfo.address}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <h3 className="text-white font-semibold mb-3">Booking Guidelines</h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li>• Advance booking required</li>
                <li>• Professional enquiries only</li>
                <li>• Clear project details needed</li>
                <li>• Consent protocols mandatory</li>
                <li>• Confidentiality assured</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h2 className="text-2xl font-semibold text-white mb-8">Enquiry Form</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="enquiryType" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                    Enquiry Type *
                  </label>
                  <select
                    id="enquiryType"
                    name="enquiryType"
                    value={formData.enquiryType}
                    onChange={handleChange}
                    required
                    className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                  >
                    <option value="">Select type...</option>
                    {enquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="projectDate" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  Preferred Project Date
                </label>
                <input
                  type="date"
                  id="projectDate"
                  name="projectDate"
                  value={formData.projectDate}
                  onChange={handleChange}
                  className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                />
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
                  placeholder="Please provide details about your project, creative vision, and any specific requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>
                    Submit Enquiry <Send size={16} />
                  </>
                )}
              </button>

              <p className="text-gray-500 text-xs mt-4 text-center">
                All enquiries are confidential. We typically respond within 24 hours.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;